import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { logIn, useUserAuth } from "../../UserAuth";
import { parseDateList, formatDateString, isValidDate } from "../../utils/dateUtils";
import { getDatesToChapters } from "./challenges";
import {
  capitalizeBook,
  getReadingPortionsFromBooks,
} from "../../utils/bible_utils";
import { addDoc, collection, doc, getDoc, writeBatch } from "firebase/firestore";
import { createUserInDatabaseIfDoesNotExist, db } from "../../firebase";
import { MaterialHeader } from "../MaterialHeader";
import { useViewportHeight } from "../../utils/viewportUtils";
import { Icon } from "../Icon";

// State persistence utilities
const STORAGE_KEY = 'createChallengeState';

const saveStateToStorage = (state) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('Error saving CreateChallenge state:', error);
  }
};

const loadStateFromStorage = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch (error) {
    console.error('Error loading CreateChallenge state:', error);
    return null;
  }
};

const clearStateFromStorage = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing CreateChallenge state:', error);
  }
};

export const CreateChallenge = () => {
  const { user } = useUserAuth();
  const history = useHistory();
  const [currentStep, setCurrentStep] = useState(1);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [books, setBooks] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [days, setDays] = useState([]);
  const [datesToSkip, setDatesToSkip] = useState("");
  const [parsedDatesToSkip, setParsedDatesToSkip] = useState([]); // [Date]
  const [datesToChapters, setDatesToChapters] = useState([]); // {Date: [String]
  const [challengeName, setChallengeName] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [creatingChallenge, setCreatingChallenge] = useState(false);
  const [createdChallengeID, setCreatedChallengeID] = useState(null);
  const viewportHeight = useViewportHeight();

  // Load persisted state on component mount
  useEffect(() => {
    const savedState = loadStateFromStorage();
    if (savedState) {
      setCurrentStep(savedState.currentStep || 1);
      setBooks(savedState.books || []);
      setStartDate(savedState.startDate || "");
      setEndDate(savedState.endDate || "");
      setDays(savedState.days || []);
      setDatesToSkip(savedState.datesToSkip || "");
      setParsedDatesToSkip(savedState.parsedDatesToSkip || []);
      setDatesToChapters(savedState.datesToChapters || []);
      setChallengeName(savedState.challengeName || "");
      setIsPublic(savedState.isPublic || false);
    }
  }, []);

  // Save state to localStorage whenever relevant state changes
  useEffect(() => {
    const stateToSave = {
      currentStep,
      books,
      startDate,
      endDate,
      days,
      datesToSkip,
      parsedDatesToSkip,
      datesToChapters,
      challengeName,
      isPublic
    };
    saveStateToStorage(stateToSave);
  }, [currentStep, books, startDate, endDate, days, datesToSkip, parsedDatesToSkip, datesToChapters, challengeName, isPublic]);


  function validateStep1() {
    return books.length > 0;
  }

  function validateStep2() {
    if (endDate === "") {
      return (
        startDate !== "" &&
        days.length > 0 &&
        (datesToSkip === "" || parsedDatesToSkip.length > 0)
      );
    } else {
      // end date is after start date
      return (
        startDate !== "" &&
        endDate > startDate &&
        days.length > 0 &&
        (datesToSkip === "" || parsedDatesToSkip.length > 0)
      );
    }
  }

  function validateStep3() {
    return challengeName !== "";
  }

  function handleCancelClick() {
    setShowCancelDialog(true);
  }

  function handleCancelConfirm() {
    setShowCancelDialog(false);
    clearStateFromStorage();
    history.push('/');
  }

  function handleCancelCancel() {
    setShowCancelDialog(false);
  }

  function updatedValuesCallback() {
    console.log("Books: ", books);
    console.log("Start date: ", startDate === null ? "null" : startDate);
    console.log("End date: ", endDate === null ? "null" : endDate);
    console.log("days: ", days);
    console.log("skip dates: ", datesToSkip);
    console.log("challenge name: ", challengeName);
    console.log("Is public: ", isPublic);
    if (validateStep2()) {
      const readingPortions = getReadingPortionsFromBooks(books);
      setDatesToChapters(
        getDatesToChapters(
          startDate,
          endDate,
          days,
          readingPortions,
          parsedDatesToSkip
        )
      );
    }
  }

  async function createChallenge() {
    if (!user) {
      return;
    }
    setCreatingChallenge(true);
    const challenge = {
      books: books,
      start_date: startDate,
      end_date: formatDateString(datesToChapters[datesToChapters.length - 1][0]), // handle blank end date
      days: days,
      challenge_name: challengeName,
      skip_dates: datesToSkip,
      reading_portions: datesToChapters.length,
      is_public: isPublic,
      created_by: user.uid,
      challenge_type: "leaderboard",
    };
    console.log(challenge);
    // save the data to the firestore database
    const newChallengeRef = await addDoc(
      collection(db, "challenges"),
      challenge
    );
    console.log("Challenge data created with ID: ", newChallengeRef.id);

    const batch = writeBatch(db);
    batch.set(doc(db, "active_challenges", newChallengeRef.id), {});
    batch.set(
      doc(db, "challenges", newChallengeRef.id, "users", user.uid),
      { role: "admin" }
    );

    const userChallengeData = {
      progress: "0".repeat(datesToChapters.length),
      emailUpdates: true,
    };

    let userDoc = await getDoc(doc(db, "users", user.uid));
    if (!userDoc.exists()) {
      await createUserInDatabaseIfDoesNotExist(db, user);
    }

    batch.set(
      doc(db, "users", user.uid, "challenges", newChallengeRef.id),
      userChallengeData
    );

    await batch.commit();
    setCreatedChallengeID(newChallengeRef.id);
    setCreatingChallenge(false);
    clearStateFromStorage();
  }

  return (
    <div style={{ 
      height: viewportHeight, 
      overflow: 'hidden',
      backgroundColor: 'var(--md-sys-color-surface)'
    }}>
      {/* Header */}
      <MaterialHeader 
        variant="create"
        title="Make a Challenge"
        onClose={handleCancelClick}
      />

      {/* Main Content */}
      <div style={{ 
        height: `calc(${viewportHeight} - 64px)`, 
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0
      }}>
        {/* Cancel Confirmation Dialog */}
        {showCancelDialog && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{ 
              padding: '24px', 
              maxWidth: '400px',
              margin: '16px',
              backgroundColor: 'var(--md-sys-color-surface-container-high)',
              borderRadius: '12px',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
              border: '1px solid var(--md-sys-color-outline-variant)'
            }}>
              <h3 style={{ 
                margin: '0 0 16px 0',
                color: 'var(--md-sys-color-on-surface)',
                fontSize: '20px',
                fontWeight: '500'
              }}>
                Cancel Challenge Creation?
              </h3>
              <p style={{ 
                margin: '0 0 24px 0',
                color: 'var(--md-sys-color-on-surface-variant)',
                lineHeight: '1.4'
              }}>
                All information will be lost. Are you sure you want to cancel?
              </p>
              <div style={{ 
                display: 'flex', 
                gap: '8px',
                justifyContent: 'flex-end'
              }}>
                <button 
                  onClick={handleCancelCancel}
                  style={{
                    padding: '10px 16px',
                    border: 'none',
                    borderRadius: '20px',
                    backgroundColor: 'transparent',
                    color: 'var(--md-sys-color-primary)',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontFamily: 'inherit'
                  }}
                >
                  Keep Editing
                </button>
                <button 
                  onClick={handleCancelConfirm}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 24px',
                    border: 'none',
                    borderRadius: '20px',
                    backgroundColor: 'var(--md-sys-color-primary)',
                    color: 'var(--md-sys-color-on-primary)',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontFamily: 'inherit'
                  }}
                >
                  <Icon name="close" />
                  Cancel Creation
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {creatingChallenge && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '50vh',
            gap: '16px'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              border: '4px solid var(--md-sys-color-outline-variant)',
              borderTop: '4px solid var(--md-sys-color-primary)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <h2 style={{
              color: 'var(--md-sys-color-on-surface)',
              fontSize: '24px',
              fontWeight: '500',
              margin: 0
            }}>
              Creating Challenge...
            </h2>
          </div>
        )}

        {/* Success State */}
        {createdChallengeID && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '50vh',
            gap: '16px'
          }}>
            <Icon
              name="check"
              size="hero"
              style={{ 
                color: 'var(--md-sys-color-primary)'
              }}
            />
            <h2 style={{
              color: 'var(--md-sys-color-on-surface)',
              fontSize: '24px',
              fontWeight: '500',
              margin: 0
            }}>
              Challenge Created!
            </h2>
            <p style={{
              color: 'var(--md-sys-color-on-surface-variant)',
              textAlign: 'center',
              margin: '0 0 16px 0'
            }}>
              Your reading challenge has been created successfully.
            </p>
            <a 
              href={`/challenge?id=${createdChallengeID}&from=create`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 24px',
                border: 'none',
                borderRadius: '20px',
                backgroundColor: 'var(--md-sys-color-primary)',
                color: 'var(--md-sys-color-on-primary)',
                fontSize: '14px',
                fontWeight: '500',
                textDecoration: 'none',
                fontFamily: 'inherit'
              }}
            >
              <Icon name="view" />
              View Challenge
            </a>
          </div>
        )}

        {/* Form Content */}
        {!creatingChallenge && !createdChallengeID && (
          <>
            {user ? (
              <>
                {/* Scrollable Content Area */}
                <div style={{ 
                  flex: 1, 
                  overflow: 'auto', 
                  padding: '16px',
                  minHeight: 0
                }}>
                  <div style={{ maxWidth: '800px', margin: '0 auto', paddingBottom: '16px' }}>
                {/* Step 1: Select Books */}
                {currentStep === 1 && (
                  <md-elevated-card style={{ padding: '24px', marginBottom: '16px' }}>
                    <h2 style={{
                      color: 'var(--md-sys-color-on-surface)',
                      fontSize: '24px',
                      fontWeight: '500',
                      margin: '0 0 16px 0'
                    }}>
                      Select Books to Read
                    </h2>
                    <p style={{
                      color: 'var(--md-sys-color-on-surface-variant)',
                      lineHeight: '1.4',
                      marginBottom: '16px'
                    }}>
                      Select the books of the Bible to read. On a desktop, you may hold shift to select a range of books.
                    </p>
                    <select
                      multiple
                      value={books}
                      style={{
                        width: '100%',
                        height: 'calc(100vh - 300px)',
                        minHeight: '300px',
                        padding: '12px',
                        border: '1px solid var(--md-sys-color-outline)',
                        borderRadius: '12px',
                        backgroundColor: 'var(--md-sys-color-surface-container-lowest)',
                        color: 'var(--md-sys-color-on-surface)',
                        fontSize: '14px',
                        fontFamily: 'inherit'
                      }}
                      onChange={(e) => {
                        const options = e.target.options;
                        const value = [];
                        for (let i = 0, l = options.length; i < l; i++) {
                          if (options[i].selected) {
                            value.push(options[i].value);
                          }
                        }
                        setBooks(value);
                      }}
                    >
                      <option value="genesis">Genesis</option>
                      <option value="exodus">Exodus</option>
                      <option value="leviticus">Leviticus</option>
                      <option value="numbers">Numbers</option>
                      <option value="deuteronomy">Deuteronomy</option>
                      <option value="joshua">Joshua</option>
                      <option value="judges">Judges</option>
                      <option value="ruth">Ruth</option>
                      <option value="1 samuel">1 Samuel</option>
                      <option value="2 samuel">2 Samuel</option>
                      <option value="1 kings">1 Kings</option>
                      <option value="2 kings">2 Kings</option>
                      <option value="1 chronicles">1 Chronicles</option>
                      <option value="2 chronicles">2 Chronicles</option>
                      <option value="ezra">Ezra</option>
                      <option value="nehemiah">Nehemiah</option>
                      <option value="esther">Esther</option>
                      <option value="job">Job</option>
                      <option value="psalms">Psalms</option>
                      <option value="proverbs">Proverbs</option>
                      <option value="ecclesiastes">Ecclesiastes</option>
                      <option value="song of songs">Song of Songs</option>
                      <option value="isaiah">Isaiah</option>
                      <option value="jeremiah">Jeremiah</option>
                      <option value="lamentations">Lamentations</option>
                      <option value="ezekiel">Ezekiel</option>
                      <option value="daniel">Daniel</option>
                      <option value="hosea">Hosea</option>
                      <option value="joel">Joel</option>
                      <option value="amos">Amos</option>
                      <option value="obadiah">Obadiah</option>
                      <option value="jonah">Jonah</option>
                      <option value="micah">Micah</option>
                      <option value="nahum">Nahum</option>
                      <option value="habakkuk">Habakkuk</option>
                      <option value="zephaniah">Zephaniah</option>
                      <option value="haggai">Haggai</option>
                      <option value="zechariah">Zechariah</option>
                      <option value="malachi">Malachi</option>
                      <option value="matthew">Matthew</option>
                      <option value="mark">Mark</option>
                      <option value="luke">Luke</option>
                      <option value="john">John</option>
                      <option value="acts">Acts</option>
                      <option value="romans">Romans</option>
                      <option value="1 corinthians">1 Corinthians</option>
                      <option value="2 corinthians">2 Corinthians</option>
                      <option value="galatians">Galatians</option>
                      <option value="ephesians">Ephesians</option>
                      <option value="philippians">Philippians</option>
                      <option value="colossians">Colossians</option>
                      <option value="1 thessalonians">1 Thessalonians</option>
                      <option value="2 thessalonians">2 Thessalonians</option>
                      <option value="1 timothy">1 Timothy</option>
                      <option value="2 timothy">2 Timothy</option>
                      <option value="titus">Titus</option>
                      <option value="philemon">Philemon</option>
                      <option value="hebrews">Hebrews</option>
                      <option value="james">James</option>
                      <option value="1 peter">1 Peter</option>
                      <option value="2 peter">2 Peter</option>
                      <option value="1 john">1 John</option>
                      <option value="2 john">2 John</option>
                      <option value="3 john">3 John</option>
                      <option value="jude">Jude</option>
                      <option value="revelation">Revelation</option>
                    </select>
                  </md-elevated-card>
                )}

                {/* Step 2: Choose Dates and Days */}
                {currentStep === 2 && (
                  <md-elevated-card style={{ padding: '24px', marginBottom: '16px' }}>
                    <h2 style={{
                      color: 'var(--md-sys-color-on-surface)',
                      fontSize: '24px',
                      fontWeight: '500',
                      margin: '0 0 24px 0'
                    }}>
                      Choose Dates and Days
                    </h2>
                    
                    <div style={{ marginBottom: '24px' }}>
                      <label style={{
                        display: 'block',
                        color: 'var(--md-sys-color-on-surface)',
                        fontSize: '16px',
                        fontWeight: '500',
                        marginBottom: '8px'
                      }}>
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={startDate}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid var(--md-sys-color-outline)',
                          borderRadius: '12px',
                          backgroundColor: 'var(--md-sys-color-surface-container-lowest)',
                          color: 'var(--md-sys-color-on-surface)',
                          fontSize: '14px',
                          fontFamily: 'inherit'
                        }}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                      <p style={{
                        color: 'var(--md-sys-color-on-surface-variant)',
                        fontSize: '12px',
                        margin: '8px 0 0 0'
                      }}>
                        The start date is the first day of the challenge. Emails will be sent starting from that day.
                      </p>
                    </div>

                    <div style={{ marginBottom: '24px' }}>
                      <label style={{
                        display: 'block',
                        color: 'var(--md-sys-color-on-surface)',
                        fontSize: '16px',
                        fontWeight: '500',
                        marginBottom: '8px'
                      }}>
                        End Date (Optional)
                      </label>
                      <input
                        type="date"
                        value={endDate}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid var(--md-sys-color-outline)',
                          borderRadius: '12px',
                          backgroundColor: 'var(--md-sys-color-surface-container-lowest)',
                          color: 'var(--md-sys-color-on-surface)',
                          fontSize: '14px',
                          fontFamily: 'inherit'
                        }}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                      <p style={{
                        color: 'var(--md-sys-color-on-surface-variant)',
                        fontSize: '12px',
                        margin: '8px 0 0 0'
                      }}>
                        The number of chapters will be arranged evenly across days. If you leave this blank, the challenge will have one chapter per day and the end date will be calculated automatically.
                      </p>
                    </div>

                    <div style={{ marginBottom: '24px' }}>
                      <label style={{
                        display: 'block',
                        color: 'var(--md-sys-color-on-surface)',
                        fontSize: '16px',
                        fontWeight: '500',
                        marginBottom: '8px'
                      }}>
                        Select Days of the Week
                      </label>
                      <select
                        multiple
                        value={days}
                        style={{
                          width: '100%',
                          height: '150px',
                          padding: '12px',
                          border: '1px solid var(--md-sys-color-outline)',
                          borderRadius: '12px',
                          backgroundColor: 'var(--md-sys-color-surface-container-lowest)',
                          color: 'var(--md-sys-color-on-surface)',
                          fontSize: '14px',
                          fontFamily: 'inherit'
                        }}
                        onChange={(e) => {
                          const options = e.target.options;
                          const selectedDays = [];
                          for (let i = 0; i < options.length; i++) {
                            if (options[i].selected) {
                              selectedDays.push(options[i].value);
                            }
                          }
                          setDays(selectedDays);
                        }}
                      >
                        <option value="Monday">Monday</option>
                        <option value="Tuesday">Tuesday</option>
                        <option value="Wednesday">Wednesday</option>
                        <option value="Thursday">Thursday</option>
                        <option value="Friday">Friday</option>
                        <option value="Saturday">Saturday</option>
                        <option value="Sunday">Lord's Day</option>
                      </select>
                      <p style={{
                        color: 'var(--md-sys-color-on-surface-variant)',
                        fontSize: '12px',
                        margin: '8px 0 0 0'
                      }}>
                        Select days of the week on which you want to receive emails. The reading challenge will only include the days selected.
                      </p>
                    </div>

                    <div>
                      <label style={{
                        display: 'block',
                        color: 'var(--md-sys-color-on-surface)',
                        fontSize: '16px',
                        fontWeight: '500',
                        marginBottom: '8px'
                      }}>
                        Dates to Skip (Optional)
                      </label>
                      <input
                        type="text"
                        value={datesToSkip}
                        placeholder="e.g., 2024-12-25, 2024-01-01...2024-01-07"
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid var(--md-sys-color-outline)',
                          borderRadius: '12px',
                          backgroundColor: 'var(--md-sys-color-surface-container-lowest)',
                          color: 'var(--md-sys-color-on-surface)',
                          fontSize: '14px',
                          fontFamily: 'inherit'
                        }}
                        onChange={(e) => setDatesToSkip(e.target.value)}
                      />
                      <p style={{
                        color: 'var(--md-sys-color-on-surface-variant)',
                        fontSize: '12px',
                        margin: '8px 0 0 0'
                      }}>
                        Enter dates to skip in the format "YYYY-MM-DD...YYYY-MM-DD" or "YYYY-MM-DD". For example, to skip November 1-7, 2016, you would enter 2016-11-01...2016-11-07. Separate multiple dates (or date ranges) to skip with commas.
                      </p>
                    </div>
                  </md-elevated-card>
                )}

                {/* Step 3: Name and Review */}
                {currentStep === 3 && (
                  <div>
                    <md-elevated-card style={{ padding: '24px', marginBottom: '16px' }}>
                      <h2 style={{
                        color: 'var(--md-sys-color-on-surface)',
                        fontSize: '24px',
                        fontWeight: '500',
                        margin: '0 0 24px 0'
                      }}>
                        Name and Review Challenge
                      </h2>
                      
                      <div style={{ marginBottom: '24px' }}>
                        <label style={{
                          display: 'block',
                          color: 'var(--md-sys-color-on-surface)',
                          fontSize: '16px',
                          fontWeight: '500',
                          marginBottom: '8px'
                        }}>
                          Challenge Name
                        </label>
                        <input
                          type="text"
                          value={challengeName}
                          placeholder="e.g., New Testament in a Year"
                          style={{
                            width: '100%',
                            padding: '12px',
                            border: '1px solid var(--md-sys-color-outline)',
                            borderRadius: '12px',
                            backgroundColor: 'var(--md-sys-color-surface-container-lowest)',
                            color: 'var(--md-sys-color-on-surface)',
                            fontSize: '14px',
                            fontFamily: 'inherit'
                          }}
                          onChange={(e) => setChallengeName(e.target.value)}
                        />
                        <p style={{
                          color: 'var(--md-sys-color-on-surface-variant)',
                          fontSize: '12px',
                          margin: '8px 0 0 0'
                        }}>
                          Enter a name for your challenge. This will be used in the subject line of the emails and in the title of the challenge page.
                        </p>
                      </div>

                      <div style={{ marginBottom: '24px' }}>
                        <p style={{
                          color: 'var(--md-sys-color-on-surface)',
                          fontSize: '16px',
                          fontWeight: '500',
                          marginBottom: '12px'
                        }}>
                          Challenge Visibility
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            padding: '8px',
                            borderRadius: '8px'
                          }}>
                            <input
                              type="radio"
                              name="visibility"
                              value="private"
                              checked={!isPublic}
                              style={{ marginRight: '8px' }}
                              onChange={() => setIsPublic(false)}
                            />
                            <div>
                              <div style={{ color: 'var(--md-sys-color-on-surface)', fontWeight: '500' }}>
                                Private Challenge
                              </div>
                              <div style={{ color: 'var(--md-sys-color-on-surface-variant)', fontSize: '12px' }}>
                                Only you can access this challenge
                              </div>
                            </div>
                          </label>
                          <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            padding: '8px',
                            borderRadius: '8px'
                          }}>
                            <input
                              type="radio"
                              name="visibility"
                              value="public"
                              checked={isPublic}
                              style={{ marginRight: '8px' }}
                              onChange={() => setIsPublic(true)}
                            />
                            <div>
                              <div style={{ color: 'var(--md-sys-color-on-surface)', fontWeight: '500' }}>
                                Public Challenge
                              </div>
                              <div style={{ color: 'var(--md-sys-color-on-surface-variant)', fontSize: '12px' }}>
                                Other users can search for and join this challenge
                              </div>
                            </div>
                          </label>
                        </div>
                      </div>
                    </md-elevated-card>

                    {/* Reading Schedule */}
                    {datesToChapters && datesToChapters.length > 0 && (
                      <md-elevated-card style={{ padding: '24px', marginBottom: '16px' }}>
                        <h3 style={{
                          color: 'var(--md-sys-color-on-surface)',
                          fontSize: '20px',
                          fontWeight: '500',
                          margin: '0 0 16px 0'
                        }}>
                          Reading Schedule
                        </h3>
                        <div style={{
                          maxHeight: '300px',
                          overflow: 'auto',
                          border: '1px solid var(--md-sys-color-outline-variant)',
                          borderRadius: '8px'
                        }}>
                          <table style={{ 
                            width: '100%', 
                            borderCollapse: 'collapse',
                            fontSize: '14px'
                          }}>
                            <thead>
                              <tr style={{ 
                                backgroundColor: 'var(--md-sys-color-surface-container)',
                                position: 'sticky',
                                top: 0
                              }}>
                                <th style={{ 
                                  padding: '12px', 
                                  textAlign: 'left',
                                  color: 'var(--md-sys-color-on-surface)',
                                  fontWeight: '500'
                                }}>
                                  Day
                                </th>
                                <th style={{ 
                                  padding: '12px', 
                                  textAlign: 'left',
                                  color: 'var(--md-sys-color-on-surface)',
                                  fontWeight: '500'
                                }}>
                                  Chapters
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {datesToChapters.map((dateToChapter, index) => (
                                <tr key={index} style={{
                                  borderTop: index > 0 ? '1px solid var(--md-sys-color-outline-variant)' : 'none'
                                }}>
                                  <td style={{ 
                                    padding: '12px',
                                    color: 'var(--md-sys-color-on-surface)'
                                  }}>
                                    {isValidDate(dateToChapter[0]) ? formatDateString(dateToChapter[0]) : 'Invalid Date'}
                                  </td>
                                  <td style={{ 
                                    padding: '12px',
                                    color: 'var(--md-sys-color-on-surface-variant)'
                                  }}>
                                    {dateToChapter[1]
                                      .map(chapter => `${capitalizeBook(chapter[0])} ${chapter[1]}`)
                                      .join(", ")}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </md-elevated-card>
                    )}
                  </div>
                )}
                  </div>
                </div>

                {/* Fixed Navigation Buttons */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 16px',
                  backgroundColor: 'var(--md-sys-color-surface)',
                  borderTop: '1px solid var(--md-sys-color-outline-variant)',
                  flexShrink: 0,
                  minHeight: '72px',
                  position: 'relative',
                  zIndex: 1
                }}>
                  {currentStep > 1 ? (
                    <button 
                      onClick={() => setCurrentStep(currentStep - 1)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 16px',
                        minHeight: '44px',
                        border: 'none',
                        borderRadius: '20px',
                        backgroundColor: 'transparent',
                        color: 'var(--md-sys-color-primary)',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        fontFamily: 'inherit'
                      }}
                    >
                      <Icon name="back" />
                      Back
                    </button>
                  ) : <div />}

                  <div style={{ display: 'flex', gap: '8px' }}>
                    {currentStep < 3 ? (
                      <button 
                        onClick={() => {
                          setParsedDatesToSkip(parseDateList(datesToSkip));
                          setCurrentStep(currentStep + 1);
                          updatedValuesCallback();
                        }}
                        disabled={
                          (currentStep === 1 && !validateStep1()) ||
                          (currentStep === 2 && !validateStep2())
                        }
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '12px 24px',
                          minHeight: '44px',
                          border: 'none',
                          borderRadius: '20px',
                          backgroundColor: (currentStep === 1 && !validateStep1()) || (currentStep === 2 && !validateStep2()) 
                            ? 'var(--md-sys-color-outline-variant)' 
                            : 'var(--md-sys-color-primary)',
                          color: (currentStep === 1 && !validateStep1()) || (currentStep === 2 && !validateStep2()) 
                            ? 'var(--md-sys-color-on-surface-variant)' 
                            : 'var(--md-sys-color-on-primary)',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: (currentStep === 1 && !validateStep1()) || (currentStep === 2 && !validateStep2()) 
                            ? 'not-allowed' 
                            : 'pointer',
                          fontFamily: 'inherit'
                        }}
                      >
                        Next
                        <Icon name="next" />
                      </button>
                    ) : (
                      <button 
                        onClick={createChallenge}
                        {...(!validateStep3() ? { disabled: true } : {})}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '12px 24px',
                          minHeight: '44px',
                          border: 'none',
                          borderRadius: '20px',
                          backgroundColor: !validateStep3() 
                            ? 'var(--md-sys-color-outline-variant)' 
                            : 'var(--md-sys-color-primary)',
                          color: !validateStep3() 
                            ? 'var(--md-sys-color-on-surface-variant)' 
                            : 'var(--md-sys-color-on-primary)',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: !validateStep3() ? 'not-allowed' : 'pointer',
                          fontFamily: 'inherit'
                        }}
                      >
                        <Icon name="add" />
                        Create Challenge
                      </button>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div style={{ 
                flex: 1, 
                overflow: 'auto', 
                padding: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
              <md-elevated-card style={{ 
                padding: '24px', 
                textAlign: 'center',
                maxWidth: '400px',
                margin: '50px auto'
              }}>
                <Icon
                  name="account"
                  size="hero"
                  style={{ 
                    color: 'var(--md-sys-color-primary)',
                    marginBottom: '16px'
                  }}
                />
                <h2 style={{
                  color: 'var(--md-sys-color-on-surface)',
                  fontSize: '20px',
                  fontWeight: '500',
                  margin: '0 0 8px 0'
                }}>
                  Sign In Required
                </h2>
                <p style={{
                  color: 'var(--md-sys-color-on-surface-variant)',
                  marginBottom: '16px'
                }}>
                  Please sign in to create a reading challenge.
                </p>
                <button 
                  onClick={logIn}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 24px',
                    border: 'none',
                    borderRadius: '20px',
                    backgroundColor: 'var(--md-sys-color-primary)',
                    color: 'var(--md-sys-color-on-primary)',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontFamily: 'inherit'
                  }}
                >
                  <Icon name="signIn" />
                  Sign In
                </button>
              </md-elevated-card>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add spinner animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};
